'use client';

import { useState } from 'react';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { ButtonBase, NavigationBar } from '../../../../common';
import { CheckboxSingle } from '../../../../common/components/CheckboxSingle/CheckboxSingle';
import { NavigationBackButton } from '../../../../navigation/NavigationBackButton/NavigationBackButton.use-client';
import { dismissOpenBankingInfoScreen } from './openBankingInfoScreenStorage';
import {
  FilledDismiss,
  RegularBuildingBank,
  RegularShieldKeyhole,
  RegularWallet,
} from '../../../../common/svgIcons';
import {
  wrapperStyles,
  headerStyles,
  titleStyles,
  descriptionStyles,
  contentStyles,
  featureCardStyles,
  cardIconStyles,
  cardContentStyles,
  cardTitleStyles,
  cardDescriptionStyles,
  bottomSectionStyles,
  checkboxContainerStyles,
} from './openBankingInfoScreenStyles';

interface OpenBankingInfoScreenProps {
  onProceed: () => void;
}

const FeatureCard = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className={featureCardStyles}>
    <div className={cardIconStyles}>{icon}</div>

    <div className={cardContentStyles}>
      <p className={cardTitleStyles}>{title}</p>
      <p className={cardDescriptionStyles}>{description}</p>
    </div>
  </div>
);

export const OpenBankingInfoScreen = ({
  onProceed,
}: OpenBankingInfoScreenProps) => {
  const { t } = useClientTranslation();

  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleProceed = () => {
    if (dontShowAgain) {
      dismissOpenBankingInfoScreen();
    }

    onProceed();
  };

  return (
    <div className={wrapperStyles}>
      <NavigationBar
        title={t('open_banking')}
        NavigationLeftSlot={<NavigationBackButton />}
        NavigationRightSlot={
          <NavigationBackButton icon={<FilledDismiss size={24} />} />
        }
      />

      <div className={headerStyles}>
        <p className={titleStyles}>{t('redirect_to_bank_@tenant')}</p>

        <p className={descriptionStyles}>
          {t('open_banking_redirect_description_@tenant')}
        </p>
      </div>

      <div className={contentStyles}>
        <FeatureCard
          icon={<RegularBuildingBank size={24} />}
          title={t('open_banking_partners')}
          description={t('open_banking_partners_description')}
        />

        <FeatureCard
          icon={<RegularShieldKeyhole size={24} />}
          title={t('bank_verification')}
          description={t('bank_verification_description')}
        />

        <FeatureCard
          icon={<RegularWallet size={24} />}
          title={t('bank_account_selection')}
          description={t('bank_account_selection_description')}
        />
      </div>

      <div className={bottomSectionStyles}>
        <ButtonBase
          visual="accent"
          size="large"
          fullWidth
          onClick={handleProceed}
          data-testid="proceed-to-open-banking-button"
          noHover
        >
          {t('proceed_to_open_banking')}
        </ButtonBase>

        <div className={checkboxContainerStyles}>
          <CheckboxSingle
            checked={dontShowAgain}
            onChange={() => setDontShowAgain((previous) => !previous)}
            label={t('dont_show_this_message_again')}
            labelPosition="right"
          />
        </div>
      </div>
    </div>
  );
};
